import React, { useState, useMemo, useContext } from 'react';
import { Box, IconButton, List, ListItemButton, ListItemIcon, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowUpIcon } from '../../../../assets/icons/ic_action_arrow_up.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/ic_action_arrow_down.svg';
import AppContext from '../../../../context/AppContext';
import SecondaryMenuDrawer from '../../../../frame/components/other/SecondaryMenuDrawer';
import HeaderWithUnderline from '../../../../frame/components/header/HeaderWithUnderline';
import FavoritesManager from '../../../../manager/FavoritesManager';
import { hexToRgba } from '../../../../util/ColorUtil';
import { buildGroupTree } from '../FavoriteGroupTree';
import { ReactComponent as FolderIcon } from '../../../../assets/icons/ic_action_folder.svg';
import { ReactComponent as FolderAddIcon } from '../../../../assets/icons/ic_action_folder_add_v2.svg';
import ThickDivider from '../../../../frame/components/dividers/ThickDivider';
import DividerWithMargin from '../../../../frame/components/dividers/DividerWithMargin';
import IconBtn from '../../../../frame/components/btns/IconBtn';
import AddFolderDialog from '../AddFolderDialog';
import isEmpty from 'lodash-es/isEmpty';
import values from 'lodash-es/values';
import SmallRadio from '../../../../frame/components/items/SmallRadio';
import styles from './folderSelectionPanel.module.css';
import menuStyles from '../../../../menu/trackfavmenu.module.css';

const ROW_PADDING_LEFT = 4;
const LEVEL_INDENT = 24;
const ARROW_BOX_WIDTH = 36;

export default function FolderSelectionPanel({ selectedGroup, defaultGroup, isTrackWpt, onSelect, onClose }) {
    const ctx = useContext(AppContext);
    const { t } = useTranslation();

    const groups = isTrackWpt ? ctx.selectedGpxFile?.pointsGroups : ctx.favorites.groups;

    const [expanded, setExpanded] = useState(() => {
        const set = new Set();
        if (selectedGroup?.name?.includes('/')) {
            const parts = selectedGroup.name.split('/');
            for (let i = 1; i < parts.length; i++) {
                set.add(parts.slice(0, i).join('/'));
            }
        }
        return set;
    });
    const [hoveredItem, setHoveredItem] = useState(null);
    const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
    const [addFolderParent, setAddFolderParent] = useState(null);

    const groupTree = useMemo(() => buildTree(groups, isTrackWpt, t), [groups, isTrackWpt, t]);
    function toggleExpanded(name, e) {
        e.stopPropagation();
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    }

    function handleAddFolder(parentGroup) {
        setAddFolderParent(parentGroup ?? null);
        setAddFolderDialogOpen(true);
    }

    function handleAddFolderRoot() {
        handleAddFolder(null);
    }

    function isSelected(folder) {
        const sel = selectedGroup;
        if (!sel) {
            return folder.name === defaultGroup || folder.name === FavoritesManager.DEFAULT_GROUP_NAME;
        }
        if (sel.id !== undefined) return sel.id === folder.group?.id;
        return sel.name === folder.name;
    }

    function renderFolder(folder, level) {
        const hasSubfolders = folder.subfolders?.length > 0;
        const isOpen = expanded.has(folder.name);
        const selected = isSelected(folder);
        const isHovered = hoveredItem === folder.name;
        const isVirtual = folder.virtual === true;

        return (
            <Box>
                <ListItemButton
                    id={`se-fav-group-item-${folder.name}`}
                    className={styles.folderItem}
                    style={{ paddingLeft: rowPaddingLeft(level) }}
                    onMouseEnter={() => setHoveredItem(folder.name)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => !isVirtual && onSelect(folder.group ?? { name: folder.name })}
                    selected={selected}
                >
                    <Box className={styles.arrowBox}>
                        {hasSubfolders && (
                            <IconBtn
                                id={`se-fav-group-expand-${folder.name}`}
                                size="small"
                                active={isOpen}
                                onClick={(e) => toggleExpanded(folder.name, e)}
                            >
                                {isOpen ? (
                                    <ArrowDownIcon className={styles.arrowIcon} />
                                ) : (
                                    <ArrowUpIcon className={styles.arrowIcon} />
                                )}
                            </IconBtn>
                        )}
                    </Box>
                    <ListItemIcon className={styles.folderIconBox}>
                        <FolderIcon className={styles.folderSvg} style={{ fill: folder.color ?? '#888888' }} />
                    </ListItemIcon>
                    <Typography noWrap className={selected ? styles.folderNameSelected : styles.folderName}>
                        {folder.displayName}
                    </Typography>
                    {isHovered && !isTrackWpt ? (
                        <IconBtn
                            id={`se-add-folder-inside-${folder.name}`}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddFolder(folder.group ?? { name: folder.name });
                            }}
                            title={t('add_new_folder')}
                            className={styles.addFolderInsideBtn}
                        >
                            <FolderAddIcon />
                        </IconBtn>
                    ) : (
                        !isVirtual &&
                        folder.size != null && <Typography className={styles.folderCount}>{folder.size}</Typography>
                    )}
                    {!isVirtual ? (
                        <SmallRadio
                            checked={selected}
                            onChange={() => onSelect(folder.group ?? { name: folder.name })}
                            onClick={(e) => e.stopPropagation()}
                            className={styles.radio}
                        />
                    ) : (
                        <Box className={styles.radioPlaceholder} />
                    )}
                </ListItemButton>
                {hasSubfolders &&
                    isOpen &&
                    folder.subfolders.map((subfolder, idx) => {
                        const dividerLevel = idx === 0 ? level : level + 1;
                        return (
                            <React.Fragment key={subfolder.name}>
                                <DividerWithMargin margin={`${folderIconOffset(dividerLevel)}px`} />
                                {renderFolder(subfolder, level + 1)}
                            </React.Fragment>
                        );
                    })}
            </Box>
        );
    }

    return (
        <>
            <SecondaryMenuDrawer onClose={onClose}>
                <Box className={styles.panel}>
                    <HeaderWithUnderline
                        title={t('folder')}
                        onClose={onClose}
                        showBackButton
                        appBarProps={{ id: 'se-back-folder-selection-panel' }}
                        rightContent={
                            !isTrackWpt ? (
                                <IconButton
                                    id="se-add-folder-btn"
                                    className={menuStyles.appBarIcon}
                                    onClick={handleAddFolderRoot}
                                    size="small"
                                >
                                    <FolderAddIcon />
                                </IconButton>
                            ) : null
                        }
                    />
                    <List className={styles.list} disablePadding>
                        {groupTree.map((folder) => (
                            <React.Fragment key={folder.name}>
                                <ThickDivider />
                                {renderFolder(folder, 0)}
                            </React.Fragment>
                        ))}
                    </List>
                </Box>
            </SecondaryMenuDrawer>
            {addFolderDialogOpen && (
                <AddFolderDialog
                    dialogOpen={addFolderDialogOpen}
                    setDialogOpen={setAddFolderDialogOpen}
                    parentGroup={addFolderParent}
                    groupTree={groupTree}
                    onCreated={(newGroup) => {
                        onSelect(newGroup);
                        if (addFolderParent) {
                            setExpanded((prev) => new Set([...prev, addFolderParent.name]));
                        }
                    }}
                />
            )}
        </>
    );
}

function rowPaddingLeft(level) {
    return ROW_PADDING_LEFT + level * LEVEL_INDENT;
}

function folderIconOffset(level) {
    return rowPaddingLeft(level) + ARROW_BOX_WIDTH;
}

function buildTree(groups, isTrackWpt, t) {
    if (!groups || (Array.isArray(groups) && groups.length === 0) || isEmpty(groups)) {
        return [];
    }

    if (isTrackWpt) {
        return values(groups).map((g) => ({
            name: g.name ?? '',
            displayName: g.name === '' || g.name === undefined ? t('shared_string_my_favorites') : g.name,
            color: g.color ? hexToRgba(g.color) : undefined,
            size: g.points?.length ?? 0,
            group: g,
            subfolders: [],
        }));
    }

    return buildGroupTree(groups, t);
}
